/**
 * Product Filters JavaScript
 *
 * Handles all product filtering functionality including:
 * - Filter application and URL management
 * - Mobile/desktop checkbox synchronization
 * - Filter clearing and reset
 *
 * @author    Bilal Harry Lindemann
 * @link      https://lindemannrock.com
 * @copyright Copyright (c) 2025 LindemannRock
 * @version 1.0.0
 */

/**
 * Apply filter dropdown selection
 * @param {string} type - Filter type being applied
 */
window.applyFilterDropdown = function(type) {
    console.log('applyFilterDropdown called with type:', type);
    const url = new URL(window.location.href);
    const params = new URLSearchParams();

    // Preserve search query only, rebuild all filters from scratch
    if (url.searchParams.get('q')) {
        params.set('q', url.searchParams.get('q'));
    }

    // Handle sort parameter - check for mobile sort radio buttons
    const sortRadio = document.querySelector('input[name="mobile-sort"]:checked');
    if (sortRadio && sortRadio.value) {
        params.set('sort', sortRadio.value);
        console.log('Sort set to:', sortRadio.value);
    } else {
        // Check if there's an existing sort in the URL to preserve
        const currentSort = url.searchParams.get('sort');
        if (currentSort) {
            params.set('sort', currentSort);
        }
    }

    // ALWAYS process ALL filter types to preserve existing selections

    // Allergens - check both desktop and mobile checkboxes
    const allergenCheckboxes = document.querySelectorAll('.exclude-allergen-checkbox:checked, .mobile-exclude-allergen-checkbox:checked');
    const selectedAllergens = [...new Set(Array.from(allergenCheckboxes).map(cb => cb.value))];
    console.log('Allergen checkboxes found:', allergenCheckboxes.length, 'Values:', selectedAllergens);
    if (selectedAllergens.length) {
        params.set('excludeAllergens', selectedAllergens.join(','));
    } else {
        params.delete('excludeAllergens');
    }

    // Health & Features - check both desktop and mobile
    const healthFeatureCheckboxes = document.querySelectorAll('.health-feature-checkbox:checked, .mobile-health-feature-checkbox:checked');
    const selectedHealthFeatures = [...new Set(Array.from(healthFeatureCheckboxes).map(cb => cb.value))];
    if (selectedHealthFeatures.length) {
        params.set('healthFeatures', selectedHealthFeatures.join(','));
    } else {
        params.delete('healthFeatures');
    }

    // Product Attributes - check both desktop and mobile
    const attributeCheckboxes = document.querySelectorAll('.attribute-checkbox:checked, .mobile-attribute-checkbox:checked');
    const selectedAttributes = [...new Set(Array.from(attributeCheckboxes).map(cb => cb.value))];
    console.log('Found attribute checkboxes:', attributeCheckboxes.length, 'Selected values:', selectedAttributes);
    console.log('Checkbox details:', Array.from(attributeCheckboxes).map(cb => ({
        name: cb.name,
        value: cb.value,
        checked: cb.checked,
        visible: cb.offsetParent !== null
    })));
    if (selectedAttributes.length) {
        params.set('attributes', selectedAttributes.join(','));
    } else {
        params.delete('attributes');
    }

    console.log('Final URL params:', params.toString());
    // Remove pagination to go back to page 1 (Craft uses URL segments like /p3)
    params.delete('p');
    let newPathname = url.pathname.replace(/\/p\d+/, ''); // Remove /p3, /p10, etc.

    window.location.href = newPathname + '?' + params.toString();
}

/**
 * Apply mobile filter selections with sync
 */
window.applyProductFilters = function() {
    console.log('Syncing mobile to desktop checkboxes...');
    syncMobileToDesktop();
}

/**
 * Sync mobile checkbox selections to desktop
 */
window.syncMobileToDesktop = function() {
    // Sync mobile allergen selections to desktop
    const mobileAllergens = document.querySelectorAll('.mobile-exclude-allergen-checkbox');
    const desktopAllergens = document.querySelectorAll('.exclude-allergen-checkbox');
    mobileAllergens.forEach(mobileBox => {
        const matchingDesktop = Array.from(desktopAllergens).find(desktopBox => desktopBox.value === mobileBox.value);
        if (matchingDesktop) {
            matchingDesktop.checked = mobileBox.checked;
        }
    });

    // Sync mobile health feature selections to desktop
    const mobileHealthFeatures = document.querySelectorAll('.mobile-health-feature-checkbox');
    const desktopHealthFeatures = document.querySelectorAll('.health-feature-checkbox');
    mobileHealthFeatures.forEach(mobileBox => {
        const matchingDesktop = Array.from(desktopHealthFeatures).find(desktopBox => desktopBox.value === mobileBox.value);
        if (matchingDesktop) {
            matchingDesktop.checked = mobileBox.checked;
        }
    });

    // Sync mobile attribute selections to desktop
    const mobileAttributes = document.querySelectorAll('.mobile-attribute-checkbox');
    const desktopAttributes = document.querySelectorAll('.attribute-checkbox');
    mobileAttributes.forEach(mobileBox => {
        const matchingDesktop = Array.from(desktopAttributes).find(desktopBox => desktopBox.value === mobileBox.value);
        if (matchingDesktop) {
            matchingDesktop.checked = mobileBox.checked;
        }
    });

    console.log('Mobile to desktop sync complete, applying filters...');
    applyFilterDropdown('mobile');
}

/**
 * Sync desktop checkbox selections to mobile
 */
window.syncDesktopToMobile = function() {
    console.log('Syncing desktop to mobile checkboxes...');

    // Sync desktop allergen selections to mobile
    const desktopAllergens = document.querySelectorAll('.exclude-allergen-checkbox');
    const mobileAllergens = document.querySelectorAll('.mobile-exclude-allergen-checkbox');
    desktopAllergens.forEach(desktopBox => {
        const matchingMobile = Array.from(mobileAllergens).find(mobileBox => mobileBox.value === desktopBox.value);
        if (matchingMobile) {
            matchingMobile.checked = desktopBox.checked;
        }
    });

    // Sync desktop health feature selections to mobile
    const desktopHealthFeatures = document.querySelectorAll('.health-feature-checkbox');
    const mobileHealthFeatures = document.querySelectorAll('.mobile-health-feature-checkbox');
    desktopHealthFeatures.forEach(desktopBox => {
        const matchingMobile = Array.from(mobileHealthFeatures).find(mobileBox => mobileBox.value === desktopBox.value);
        if (matchingMobile) {
            matchingMobile.checked = desktopBox.checked;
        }
    });

    // Sync desktop attribute selections to mobile
    const desktopAttributes = document.querySelectorAll('.attribute-checkbox');
    const mobileAttributes = document.querySelectorAll('.mobile-attribute-checkbox');
    desktopAttributes.forEach(desktopBox => {
        const matchingMobile = Array.from(mobileAttributes).find(mobileBox => mobileBox.value === desktopBox.value);
        if (matchingMobile) {
            matchingMobile.checked = desktopBox.checked;
        }
    });

    console.log('Desktop to mobile sync complete');
}

/**
 * Clear all product filters
 */
window.clearProductFilters = function() {
    // Clear all filters and reload
    const url = new URL(window.location.href);
    const params = new URLSearchParams();

    // Keep only search query if it exists
    if (url.searchParams.get('q')) {
        params.set('q', url.searchParams.get('q'));
    }

    // Remove pagination to go back to page 1 (Craft uses URL segments like /p3)
    params.delete('p');
    let newPathname = url.pathname.replace(/\/p\d+/, ''); // Remove /p3, /p10, etc.

    window.location.href = newPathname + (params.toString() ? '?' + params.toString() : '');
}
